<template>
  <div class="p-branding-template-edit g-page">
    <VMessage
      type="warning"
      :fullwidth="true"
      v-if="permissionBranding !== true">
      <template v-slot:icon>
        <ExclamationTriangleIcon />
      </template>
      <template v-slot:text>
        {{ $t('branding.permissionWarning') }}
      </template>
    </VMessage>

    <VGrid v-else v-loading="loading">
      <div slot="gridItem" type="8">
        <VForm>
          <div slot="row">
            <div class="g-page__row">
              <h4>{{ $t('branding.emailTemplate.subject') }}</h4>
              <p>
                <VInput type="text" v-model="email.subject" v-if="email" />
              </p>
            </div>
            <div class="g-page__row">
              <h4>{{ $t('branding.emailTemplate.body') }}</h4>
              <VTabs>
                <div slot="email-editor" :title="$t('branding.emailTemplate.editor')" icon="paint-brush">
                  <wysiwyg v-model="email.content" v-if="email" data-hj-suppress />
                </div>
                <div slot="source" title="Source" icon="code">
                  <VInput
                    type="textarea"
                    :min-height="500"
                    class="text-editor"
                    v-model="email.content"
                    v-if="email" />
                </div>
                <div slot="plain-text" title="Plain Text" icon="pencil">
                  <VMessage type="info" subtype="basic">
                    <template v-slot:icon>
                      <InfoCircleIcon />
                    </template>
                    <template v-slot:text>
                      {{ $t('branding.emailTemplate.messageHTMLFallback') }}
                    </template>
                  </VMessage>
                  <VInput
                    type="textarea"
                    :min-height="250"
                    class="text-editor"
                    v-model="email.content_plain"
                    v-if="email" />
                </div>
              </VTabs>
            </div>
          </div>
          <div slot="formButtons">
              <ElButton @click="doReset" type="danger" icon="el-icon-delete">{{ $t('branding.emailTemplate.resetTemplate') }}</ElButton>
              <ElButton @click="doUpdate" class="float-right" type="primary">{{ $t('branding.emailTemplate.saveTemplate') }}</ElButton>
          </div>
        </VForm>
      </div>
      <div slot="gridItem" type="4">
        <VStickyColumn
          relativeElementSelector=".o-grid__item--relative-sticky-column"
          pageClass=".p-branding-template-edit">
          <div class="g-sidebar__container">
            <h3 class="g-sidebar__container__header">
              <InfoCircleIcon
                height="22"
                width="22"
              />
              <span>{{ $t('functional.whatIsThis') }}</span>
            </h3>
            <p>
              {{ $t('branding.emailTemplate.whatIsThisMessage') }}
            </p>
            <code>{contract url}</code><br />
            <code>{company name}</code><br />
            <code>{client name}</code><br />
            <code>{contract title}</code><br />
            <code>{user name}</code><br />
            <code>{party message}</code><br />
            <code>{company logo}</code>
          </div>
          <HelpAndSupportWidget />
        </VStickyColumn>
      </div>
    </VGrid>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import VInput from '@/components/deprecated/atoms/Input.vue';
import VMessage from '@/components/deprecated/atoms/Message.vue';
import VStickyColumn from '@/components/deprecated/molecules/StickyColumn.vue';
import VTabs from '@/components/deprecated/molecules/Tabs.vue';
import VForm from '@/components/deprecated/organisms/Form.vue';
import VGrid from '@/components/deprecated/organisms/Grid.vue';
import interact from 'interactjs';

import HelpAndSupportWidget from '@/views/components/HelpAndSupportWidget.vue';
import { ExclamationTriangleIcon, InfoCircleIcon } from '@/components/icons/fontIcons';

export default {
  name: 'EmailTemplateEdit',
  inject: {
    $validator: '$validator',
  },
  components: {
    VInput,
    VMessage,
    VStickyColumn,
    VTabs,
    VForm,
    VGrid,
    HelpAndSupportWidget,
    InfoCircleIcon,
    ExclamationTriangleIcon,
  },
  data() {
    return {
      email: null,
      loading: false,
    };
  },
  mounted() {
    this.$store.dispatch('emails/GET', { id: this.$route.params.emailId }).then(() => {
      interact('.editr--content img').resizable({
        edges: {
          left: true,
          right: true,
          bottom: true,
          top: true,
        },
        modifiers: [
          interact.modifiers.restrictSize({
            min: { width: 100, height: 50 },
          }),
        ],
        preserveAspectRatio: true,
        inertia: true,
      }).on('resizemove', (event) => {
        const target = event.target; // eslint-disable-line prefer-destructuring
        const x = (parseFloat(target.getAttribute('data-x')) || 0);
        const y = (parseFloat(target.getAttribute('data-y')) || 0);


        if (target.draggable === true) { // Prevent image from being dragged while resizing
          target.addEventListener('mouseup', () => {
            target.draggable = true;
            target.style.cursor = 'auto';
            this.email.content = document.getElementsByClassName('editr--content')[0].innerHTML; // Update email content
          }, {
            once: true,
          });

          target.draggable = false;
        }

        target.style.width = `${event.rect.width}px`;
        target.style.height = `${event.rect.height}px`;

        target.setAttribute('data-x', x);
        target.setAttribute('data-y', y);
        target.textContent = `${Math.round(event.rect.width)}\u00D7${Math.round(event.rect.height)}`;
      });
    });
  },
  computed: {
    ...mapGetters({
      viewingEmail: 'emails/viewingEmail',
      userHasRole: 'roles/userHasRole',
    }),
    permissionBranding() {
      return this.userHasRole('super-admin');
    },
  },
  watch: {
    viewingEmail() {
      this.email = this.viewingEmail; // Copy from the store version automatically
    },
  },
  methods: {
    doReset() {
      this.$confirm(this.$t('branding.emailTemplate.resetConfirmation'), this.$t('functional.warning'), {
        cancelButtonClass: 'el-button--medium',
        confirmButtonClass: 'el-button--medium el-button--danger',
        confirmButtonText: this.$t('functional.reset'),
        cancelButtonText: this.$t('functional.cancel'),
        type: 'warning',
      }).then(() => {
        this.loading = true;
        this.$store.dispatch('emails/RESET', this.email).finally(() => {
          this.loading = false;
        });
      }).catch(() => {});
    },
    doUpdate() {
      this.loading = true;
      this.$store.dispatch('emails/UPDATE', this.email).finally(() => {
        this.loading = false;
        this.$router.push({
          name: 'Branding',
        });
      });
    },
  },
};
</script>

<style scoped>
.m-color-picker,
.a-color-picker {
  float: left;
  margin-right: 5px;
}

.p-branding-template-edit__color-text {
  width: calc(100% - 50px);
}

.text-editor {
  float: none;
  font-family: monospace;
  tab-size: 2;
}

.text-editor * {
  height: 280px
}
</style>
